import * as React from 'react';
import { useCallback } from 'react';

import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { keyBy, mapValues } from 'lodash';

import { useOnboardingData } from 'bundles/program-personalized-tab/contexts/OnboardingContext';
import ScoredSkillRecsContext from 'bundles/program-personalized-tab/contexts/ScoredSkillRecsContext';
import EnterpriseProductRecsForSkills from 'bundles/program-personalized-tab/queries/EnterpriseProductRecsForSkills.graphql';
import UserSkillScoresForSkillRecs from 'bundles/program-personalized-tab/queries/UserSkillScoresForSkillRecs.graphql';
import type {
  EnterpriseProductRecsForSkillsQuery,
  EnterpriseProductRecsForSkillsQueryVariables,
} from 'bundles/program-personalized-tab/queries/__generated__/EnterpriseProductRecsForSkills';
import type {
  UserSkillScoresForSkillRecsQuery,
  UserSkillScoresForSkillRecsQueryVariables,
} from 'bundles/program-personalized-tab/queries/__generated__/UserSkillScoresForSkillRecs';
import type {
  DiscoveryCollectionsProductTypename,
  MixedCollectionsBySkill,
  ProductEntry,
  Skill,
  SkillData,
} from 'bundles/program-personalized-tab/types/sharedTypes';
import { getRequestSkillsParams } from 'bundles/program-personalized-tab/utils/skillRecsUtils';

type Props = {
  programId: string;
  children: React.ReactNode;
};

const useRecsForSkillsQuery = (skills: Skill[] | undefined, programId: string) => {
  const { error: skillScoresError, data: skillScoresData } = useQuery<
    UserSkillScoresForSkillRecsQuery,
    UserSkillScoresForSkillRecsQueryVariables
  >(UserSkillScoresForSkillRecs, {
    variables: { skillIds: skills?.map((skill) => skill.name) ?? [] },
    skip: !skills || skills.length === 0,
    notifyOnNetworkStatusChange: true,
    context: { clientName: 'gatewayGql' },
  });

  const skillData: SkillData = {};

  const skillScoresById = mapValues(
    keyBy(skillScoresData?.SkillScores?.userSkillScoresBySkillIds, 'skill.skillName'),
    'readableScore'
  );

  skills?.forEach((skill) => {
    skillData[skill.id] = {
      name: skill.name,
      score: skillScoresById[skill.id] ?? 0,
    };
  });

  const skillParams = getRequestSkillsParams(skills);

  const { loading, error, data } = useQuery<
    EnterpriseProductRecsForSkillsQuery,
    EnterpriseProductRecsForSkillsQueryVariables
  >(EnterpriseProductRecsForSkills, {
    variables: {
      programId,
      skillParams,
      contextId: 'personalized-tab-skill-based-recs-t2',
    },
    skip: skillParams.length === 0,
    notifyOnNetworkStatusChange: true,
    context: { clientName: 'gatewayGql' },
  });

  const getCollectionsBySkill = useCallback<() => $TSFixMe>(() => {
    const collectionsBySkill: MixedCollectionsBySkill = {};
    const skillByName = keyBy(skills, 'name');

    for (const collection of data?.DiscoveryCollections?.queryEnterpriseCollectionsByProgram?.productCollections ??
      []) {
      if (collection?.label && skillByName[collection.label]) {
        const skillId = skillByName[collection.label].id;

        const productEntryEntities: ProductEntry[] = [];
        collection.entities?.forEach((entity) => {
          if (entity && entity.__typename && entity.__typename !== 'DiscoveryCollections_Clip') {
            productEntryEntities.push({
              type: 'productCard',
              ...entity,
              __typename: entity.__typename as DiscoveryCollectionsProductTypename,
            });
          }
        });

        collectionsBySkill[skillId] = { id: collection.id, label: collection.label, entities: productEntryEntities };
      }
    }

    return collectionsBySkill;
  }, [data?.DiscoveryCollections?.queryEnterpriseCollectionsByProgram?.productCollections, skills]);

  return {
    loading,
    error: { allSkillsProgressError: skillScoresError, productRecsForSkillsError: error },
    getRecs: getCollectionsBySkill,
    skillData,
  };
};

const ScoredSkillRecsDataProvider = ({ programId, children }: Props) => {
  const onboardingData = useOnboardingData();
  const { loading, error, getRecs, skillData } = useRecsForSkillsQuery(onboardingData?.skills, programId);

  if (error) {
    Sentry.captureException(error);
  }

  return (
    <ScoredSkillRecsContext.Provider value={{ loading, error, getRecs, skillData }}>
      {children}
    </ScoredSkillRecsContext.Provider>
  );
};

export default ScoredSkillRecsDataProvider;
